/**
 * Shared sanitization utilities for input validation and XSS prevention.
 * Uses DOMPurify under the hood for safe HTML stripping.
 */

import DOMPurify from 'dompurify';

/**
 * Sanitizes a plain-text string by stripping HTML/script tags.
 * Intended for user-submitted text fields (names, descriptions, etc.)
 * that should never contain markup.
 */
export function sanitizeTextInput(value: string): string {
  if (!value) return '';
  return DOMPurify.sanitize(value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim();
}

/**
 * Sanitizes rich HTML content, allowing only safe tags and attributes.
 * Use for fields where limited formatting is expected (chat messages, etc.).
 */
export function sanitizeRichHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['a', 'p', 'br', 'strong', 'em', 'u', 'code', 'pre', 'img', 'mark', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt'],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Sanitizes HTML produced by converting an untrusted uploaded document (docx→HTML via mammoth,
 * xlsx→HTML via SheetJS) for preview rendering. Wider tag/attribute allowlist than
 * `sanitizeRichHtml` — documents legitimately contain headings, tables, and embedded images
 * (mammoth inlines images as base64 `data:` URIs) — but still strips scripts, event handlers,
 * and anything that isn't structural/presentational markup.
 */
export function sanitizeDocumentHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'a',
      'p',
      'br',
      'hr',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'sub',
      'sup',
      'span',
      'div',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'img',
      'blockquote',
      'code',
      'pre'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt', 'colspan', 'rowspan', 'id'],
    ALLOW_DATA_ATTR: false,
    ADD_DATA_URI_TAGS: ['img']
  });
}

/** Sanitizes rendered message HTML and opens every link in a new tab, without giving the target page a handle on ours. */
export function sanitizeHtmlWithNewTabLinks(html: string): string {
  const fragment = DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true });
  fragment.querySelectorAll('a[href]').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });
  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
}

/** Removes the editor's [text:color=…,background=…]…[/text] colour markup, keeping the words inside. */
export function stripTextStyleTags(markdown: string): string {
  return markdown.replace(/\[text(?::[^\]]*)?\]/g, '').replace(/\[\/text\]/g, '');
}

const HTML_TAG_PATTERN = /<[^>]*>/;

/**
 * Checks whether a string contains HTML tags.
 * Useful for Zod `.refine()` validation to reject markup in plain-text fields.
 */
export function containsHtml(value: string): boolean {
  return HTML_TAG_PATTERN.test(value);
}
